import React, { useEffect } from 'react';
import { WHATSAPP_PHONE, slugifyCategory } from '../utils/constants';

interface SEOHeadProps {
  title?:          string;
  description?:    string;
  keywords?:       string;
  image?:          string;
  url?:            string;
  type?:           'website' | 'product' | 'article';
  price?:          number;
  currency?:       string;
  availability?:   'InStock' | 'OutOfStock';
  brand?:          string;
  category?:       string;
  sku?:            string;
  localBusiness?:  boolean;
}

const SITE_NAME  = 'Balaji Fashion House';
const SITE_URL   = 'https://balajifashionhouse.com';
const SITE_DESC  = "Discover beautiful Indian ethnic wear — kurtas, suits, palazzo sets, velvet collection and more. Quality women's ethnic fashion at best prices.";
const SITE_IMAGE = `${SITE_URL}/logo.png`;
const SITE_PHONE = WHATSAPP_PHONE;
const SITE_EMAIL = 'balajifashioncollectionhouse@gmail.com';
const SITE_FB    = 'https://www.facebook.com/balajifashioncollhouse';
const SITE_INSTA = 'https://www.instagram.com/balajifashionhousemhl/';

// ── Helpers ───────────────────────────────────────────────────
const setMeta = (attr: string, val: string, isProperty = false): void => {
  const key = isProperty ? 'property' : 'name';
  let el    = document.querySelector(`meta[${key}="${attr}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, attr);
    document.head.appendChild(el);
  }
  el.setAttribute('content', val);
};

const setCanonical = (href: string): void => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id: string, data: object): void => {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el      = document.createElement('script');
    el.id   = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

// ── Component ─────────────────────────────────────────────────
const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description   = SITE_DESC,
  keywords,
  image         = SITE_IMAGE,
  url,
  type          = 'website',
  price,
  currency      = 'INR',
  availability  = 'InStock',
  brand         = SITE_NAME,
  category,
  sku,
  localBusiness = false,
}) => {
  useEffect(() => {
    // Build full values
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Indian Ethnic Wear`;
    const fullUrl   = url   ? `${SITE_URL}${url}`       : `${SITE_URL}${window.location.pathname}`;
    const fullImage = image && image.startsWith('http') ? image : SITE_IMAGE;

    // ── Page title ────────────────────────────────────────────
    document.title = fullTitle;

    // ── Basic meta ────────────────────────────────────────────
    setMeta('description',   description);
    setMeta('keywords',      keywords ?? 'Indian ethnic wear, kurta, palazzo set, salwar kameez, women fashion India, Balaji Fashion House');
    setMeta('author',        SITE_NAME);
    setMeta('robots',        'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('theme-color',   '#a47c4a');
    setMeta('geo.region',    'IN-PB');
    setMeta('geo.country',   'IN');
    setMeta('language',      'English');

    // ── Open Graph ────────────────────────────────────────────
    setMeta('og:type',         type,        true);
    setMeta('og:site_name',    SITE_NAME,   true);
    setMeta('og:locale',       'en_IN',     true);
    setMeta('og:title',        fullTitle,   true);
    setMeta('og:description',  description, true);
    setMeta('og:url',          fullUrl,     true);  // ← Page specific URL
    setMeta('og:image',        fullImage,   true);  // ← Page specific image
    setMeta('og:image:alt',    fullTitle,   true);
    setMeta('og:image:width',  '800',       true);
    setMeta('og:image:height', '800',       true);

    // ── Twitter Card ──────────────────────────────────────────
    setMeta('twitter:card',        'summary_large_image');
    setMeta('twitter:title',       fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image',       fullImage);  // ← Page specific image

    // ── Canonical ─────────────────────────────────────────────
    setCanonical(fullUrl);

    // ── Product-specific meta ─────────────────────────────────
    if (type === 'product' && price) {
      setMeta('og:price:amount',        String(price), true);
      setMeta('og:price:currency',      currency,      true);
      setMeta('product:price:amount',   String(price), true);
      setMeta('product:price:currency', currency,      true);
      setMeta('product:availability',   availability === 'InStock' ? 'instock' : 'oos', true);
      if (brand)    setMeta('product:brand',    brand,    true);
      if (category) setMeta('product:category', category, true);
    }

    // ── Organization Schema ───────────────────────────────────
    setJsonLd('org-jsonld', {
      '@context':  'https://schema.org',
      '@type':     'Organization',
      name:        SITE_NAME,
      url:         SITE_URL,
      logo:        SITE_IMAGE,
      description: SITE_DESC,
      telephone:   SITE_PHONE,
      email:       SITE_EMAIL,
      address: {
        '@type':         'PostalAddress',
        addressCountry:  'IN',
        addressRegion:   'Punjab',
      },
      sameAs: [SITE_FB, SITE_INSTA],
      contactPoint: {
        '@type':            'ContactPoint',
        telephone:           SITE_PHONE,
        contactType:         'customer service',
        availableLanguage:   ['English', 'Hindi'],
      },
    });

    // ── WebSite Schema ────────────────────────────────────────
    setJsonLd('website-jsonld', {
      '@context': 'https://schema.org',
      '@type':    'WebSite',
      name:       SITE_NAME,
      url:        SITE_URL,
      potentialAction: {
        '@type':       'SearchAction',
        target:        `${SITE_URL}/products?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });

    // ── Product Schema ────────────────────────────────────────
    if (type === 'product' && title && price) {
      setJsonLd('product-jsonld', {
        '@context':  'https://schema.org',
        '@type':     'Product',
        name:        title,
        description: description,
        image:       fullImage,
        sku:         sku ?? '',
        brand: { '@type': 'Brand', name: brand },
        offers: {
          '@type':         'Offer',
          url:             fullUrl,
          priceCurrency:   currency,
          price:           price,
          priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                             .toISOString().split('T')[0],
          availability:    `https://schema.org/${availability}`,
          seller: { '@type': 'Organization', name: SITE_NAME },
        },
      });
    } else {
      // Remove product schema on non-product pages
      document.getElementById('product-jsonld')?.remove();
    }

    // ── Breadcrumb Schema ─────────────────────────────────────
    if (type === 'product' && title && category) {
      setJsonLd('breadcrumb-jsonld', {
        '@context': 'https://schema.org',
        '@type':    'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home',        item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Collections', item: `${SITE_URL}/products` },
          { '@type': 'ListItem', position: 3, name: category,      item: `${SITE_URL}/products/${slugifyCategory(category)}` },
          { '@type': 'ListItem', position: 4, name: title,         item: fullUrl },
        ],
      });
    } else {
      document.getElementById('breadcrumb-jsonld')?.remove();
    }

    // ── LocalBusiness Schema ──────────────────────────────────
    if (localBusiness) {
      setJsonLd('localbusiness-jsonld', {
        '@context': 'https://schema.org',
        '@type':    'ClothingStore',
        name:        SITE_NAME,
        url:         SITE_URL,
        telephone:   SITE_PHONE,
        email:       SITE_EMAIL,
        description: SITE_DESC,
        image:       SITE_IMAGE,
        address: {
          '@type':         'PostalAddress',
          addressRegion:   'Punjab',
          addressCountry:  'IN',
        },
        openingHoursSpecification: [
          {
            '@type':      'OpeningHoursSpecification',
            dayOfWeek:    ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
            opens:        '10:00',
            closes:       '19:00',
          },
          {
            '@type':      'OpeningHoursSpecification',
            dayOfWeek:    ['Sunday'],
            opens:        '11:00',
            closes:       '16:00',
          },
        ],
        contactPoint: {
          '@type':           'ContactPoint',
          telephone:          SITE_PHONE,
          contactType:        'customer service',
          availableLanguage:  ['English', 'Hindi'],
        },
        sameAs: [SITE_FB, SITE_INSTA],
      });
    } else {
      document.getElementById('localbusiness-jsonld')?.remove();
    }

  }, [title, description, keywords, image, url, type, price, currency, availability, brand, category, sku, localBusiness]);

  return null;
};

export default SEOHead;
